import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { XMarkIcon, ArrowUpOnSquareIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { track, EVENTS } from '../services/analytics';
import { PILOT_KEY } from '../services/astraToday';
import { isOnboardingPath } from '../utils/onboardingLanding';

// A gentle "Add Stratia to your home screen" prompt.
//   • Android/Chrome: captures the native beforeinstallprompt and offers a real
//     Install button.
//   • iOS Safari: no beforeinstallprompt exists, so we show the manual
//     Share → "Add to Home Screen" hint instead.
// Only shown to signed-in users on a phone that hasn't installed or recently
// dismissed — never nags cold/first-time visitors.

const DISMISS_KEY = 'pwa_install_dismissed_at';
const DISMISS_DAYS = 30;

const isStandalone = () =>
    (typeof window !== 'undefined' &&
        (window.matchMedia?.('(display-mode: standalone)')?.matches || window.navigator.standalone === true));
const isMobile = () => /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '');
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '') && !window.MSStream;
const recentlyDismissed = () => {
    try {
        const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
        return ts && Date.now() - ts < DISMISS_DAYS * 864e5;
    } catch {
        return false;
    }
};

// Another sheet the student is already looking at: the quick tour, Astra's
// notification ask, the 13+/Terms gate. This card waits its turn.
const OTHER_SHEETS = '[data-testid="quick-tour"], [data-testid="push-ask"], [data-testid="push-ask-ios-install"], '
    + '[data-testid="assent-agree"]';
export const otherSheetOpen = () => {
    try { return Boolean(document.querySelector(OTHER_SHEETS)); } catch { return false; }
};

export default function InstallPrompt() {
    const { currentUser } = useAuth();
    const [deferred, setDeferred] = useState(null);
    const [show, setShow] = useState(false);
    const location = useLocation();
    const ios = isIOS();

    // One sheet at a time (#3789: on an iPhone the first open of /today showed
    // this card under the quick tour). Re-checked every second while it would
    // show, because the tour, the push ask and the pilot marker can each
    // arrive after this mounts.
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!show) return undefined;
        const t = setInterval(() => setTick((n) => n + 1), 1000);
        return () => clearInterval(t);
    }, [show]);

    useEffect(() => {
        if (isStandalone() || !isMobile() || recentlyDismissed()) return undefined;
        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferred(e);
            setShow(true);
        };
        window.addEventListener('beforeinstallprompt', onBeforeInstall);
        // iOS never fires beforeinstallprompt — show the manual hint directly.
        if (ios) setShow(true);
        return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
    }, [ios]);

    // Never during onboarding (digest S1, founder call 2026-07-30: a 27s
    // stall then "Not now" mid-journey — the install ask can wait until the
    // student has seen the product). /welcome is the journey; ?onboard=1 is
    // the first read's landing on its Launchpad card.
    if (isOnboardingPath(location.pathname)
        || new URLSearchParams(location.search).has('onboard')) return null;
    if (!show || !currentUser || isStandalone()) return null;
    // Astra Today's pilot (C1, #3756): on an iPhone the Home Screen ask is
    // Astra's, after a useful moment and with its reason (PushAsk). One ask,
    // never two.
    let pilot = false;
    try { pilot = sessionStorage.getItem(PILOT_KEY) === '1'; } catch { /* private mode */ }
    if (pilot && ios) return null;
    if (otherSheetOpen()) return null;

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* private mode */ }
        setShow(false);
        try { track(EVENTS.PWA_INSTALL_DISMISSED, { platform: ios ? 'ios' : 'android' }); } catch { /* noop */ }
    };

    const install = async () => {
        if (!deferred) return;
        deferred.prompt();
        try {
            const { outcome } = await deferred.userChoice;
            track(EVENTS.PWA_INSTALL_PROMPTED, { outcome });
        } catch { /* noop */ }
        setDeferred(null);
        setShow(false);
    };

    return (
        <div
            style={{ bottom: 'calc(5rem + var(--first-read-bar-h, 0px))' }}
            className="fixed inset-x-4 z-40 mx-auto max-w-md rounded-2xl border border-[#CFE3D4] bg-white p-4 shadow-xl"
            role="dialog"
            aria-label="Install Stratia"
            data-testid="pwa-install-prompt"
        >
            <div className="flex items-start gap-3">
                <img src="/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#1A4D2E]">Add Stratia to your home screen</p>
                    {ios ? (
                        <p className="mt-0.5 text-xs leading-relaxed text-[#4A4A4A]">
                            In Safari, tap the Share icon
                            <ArrowUpOnSquareIcon className="mx-1 inline h-4 w-4 -translate-y-px text-[#1A4D2E]" aria-label="Share" />
                            at the bottom of the screen, then scroll to{' '}
                            <span className="font-medium">&ldquo;Add to Home Screen.&rdquo;</span>
                        </p>
                    ) : (
                        <p className="mt-0.5 text-xs leading-relaxed text-[#4A4A4A]">
                            Install the app for one-tap access.
                        </p>
                    )}
                </div>
                <button onClick={dismiss} aria-label="Dismiss" className="-mr-1 -mt-1 shrink-0 rounded-full p-1 text-gray-400 hover:text-gray-600">
                    <XMarkIcon className="h-5 w-5" />
                </button>
            </div>
            <div className="mt-3 flex items-center justify-end gap-2">
                <button onClick={dismiss} className="px-3 py-1.5 text-xs font-medium text-[#6B6B6B] hover:text-[#2C2C2C]">
                    Not now
                </button>
                {!ios && (
                    <button
                        onClick={install}
                        className="rounded-lg bg-[#1A4D2E] px-4 py-1.5 text-xs font-semibold text-white hover:bg-[#2D6B45]"
                    >
                        Install
                    </button>
                )}
            </div>
        </div>
    );
}
